import React, { useState, useEffect } from 'react';
import { Table, Button } from 'react-bootstrap';
import { FaGamepad } from 'react-icons/fa';
import CryptoJS from 'crypto-js'
import { useNavigate } from 'react-router-dom';
import api from '../api';

function AdminGames() {
  const [games, setGames] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalGames, setTotalGames] = useState(0);
  const navigate = useNavigate();
  const getApiUrl = () => {
    const hashedApiVersion = localStorage.getItem('apiVersion');
    if (hashedApiVersion) {
      const version2Hash = CryptoJS.SHA256('2').toString();
      if (hashedApiVersion === version2Hash) {
        return 'https://phoenix-bingo-2.onrender.com/api';
      }
    }
    return process.env.REACT_APP_API;
  };

  const API = getApiUrl();

  const [stats, setStats] = useState({
    statsByPeriod: [],
    totalProfit: 0,
    totalBetAmount: 0,
    totalGames: 0,
    wallet: 0
  });

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API}/admin/validate-token`);

        if (response.valid !== true) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      } 
    };

    validateToken();
  }, []);
  
  useEffect(() => {
    const fetchStats = async () => {
      const response = await api.get(`${API}/admin/stats`);
      const data = await response;
      setStats(data);
    };

    fetchStats();
  }, [API]);

  useEffect(() => {
    fetchGames();
  }, [page]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API}/admin/myGames?page=${page}`);
      
      const data = await response;
      if (page === 1) {
        setGames(data.games);
      } else {
        setGames(prevGames => [...prevGames, ...data.games]);
      }
      setTotalPages(data.totalPages);
      setTotalGames(data.totalGames);
      setLoading(false);
    } catch(error) {
      setLoading(false);
    }
  }

  const calculateTotalBetAmount = (game) => {
    if (Array.isArray(game.bettingAmount)) {
      return game.bettingAmount.reduce((total, amount) => total + amount, 0);
    }
    return game.bettingAmount;
  };

  const calculateProfit = (game) => {
    if (game.isFreeGame) return 0;
    if (Array.isArray(game.bettingAmount)) {
      const totalBet = calculateTotalBetAmount(game);
      const profit = game.numberOfPlayers === 2 ? 0 : (totalBet * (game.commissionRate || 0.2)).toFixed(0);
      return profit;
    }
    // For regular games
    const totalBet = game.bettingAmount * (game.players + (game.houseCardCount || 0));
    const profit = game.numberOfPlayers === 2 ? 0 : (game.profit || (totalBet * (game.commissionRate || 0.2)).toFixed(0));
    return profit;
  };

  const formatBetAmount = (game) => {
    if (Array.isArray(game.bettingAmount)) {
      const uniqueAmounts = [...new Set(game.bettingAmount)];
      return uniqueAmounts
        .map(amount => `${amount} Birr`)
        .join(', ');
    }
    return `${game.bettingAmount} Birr`;
  };

  const loadMoreGames = () => {
    if (page < totalPages) {
      setPage(prevPage => prevPage + 1);
    }
  }

  function formatDate(dateStr) {
    const date = new Date(dateStr);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const year = date.getFullYear();
    const month = months[date.getMonth()]; 
    const day = date.getDate();
    const dayOfWeek = days[date.getDay()];
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const formattedHours = hours < 10 ? `0${hours}` : hours;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
  
    return `${dayOfWeek}, ${month} ${day}, ${year} ${formattedHours}:${formattedMinutes}:${formattedSeconds}`; 
  }
  
  return (
    <div className="my-1 m-1 p-1">
      <h2 className='text-white'>Statistics</h2>
      <div className='d-flex'>
        <div className="d-flex align-items-center p-3 bg-teal-800" style={{minHeight: '75px', marginRight: 100, borderRadius: '16px'}}>
          <FaGamepad className="text-success mr-2 fs-1" />
          <span className="text-white fs-5">Total Games: {stats.totalGames}</span>
        </div>
        <div className="d-flex align-items-center p-3 bg-teal-800" style={{minHeight: '75px', marginRight: 100, borderRadius: '16px'}}>
          <FaGamepad className="text-success mr-2 fs-1" />
          <span className="text-white fs-5">Wallet: {stats.wallet} Birr</span>
        </div>
      </div>
      <h4 className='text-white mt-5'>Recent Games</h4>
      
      <div className="overflow-x-auto rounded-lg shadow-lg">
        <table className="min-w-full divide-y divide-teal-800">
          <thead className="bg-teal-900">
            <tr>
              <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-teal-100">Date</th>
              <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-teal-100">Bet Amount</th>
              <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-teal-100">Players</th>
              <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-teal-100">Status</th>
              <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-teal-100">Total Bet Amount</th>
              <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-teal-100">Profit</th>
            </tr>
          </thead>
          <tbody className="bg-teal-800 divide-y divide-teal-700">
            {games.map(game => (
              <tr 
                key={game.id}
                className="hover:bg-teal-700 transition-colors duration-150 ease-in-out"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-teal-100">{formatDate(game.date)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-teal-100">{formatBetAmount(game)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-teal-100">{game.players}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-teal-100">{game.status}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-teal-100">{calculateTotalBetAmount(game).toLocaleString()} Birr</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-teal-100">{calculateProfit(game).toLocaleString()} Birr</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">
          Showing {games.length} out of {totalGames} games
        </p>
        
        <div>
          {page < totalPages && (
            <Button 
              className="btn btn-primary mr-2" 
              onClick={loadMoreGames} 
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More'}
            </Button>
          )}
          <Button className="btn btn-secondary">
            Export CSV
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminGames;